import { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CheckCircle2, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { safeError } from '@/lib/safeError';
import { TurnstileWidget } from '@/components/TurnstileWidget';

/** Format a phone number string as (###) ###-#### */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;

const inquirySchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100),
  email: z.string().trim().email('Valid email required').max(255),
  phone: z.string().trim()
    .refine(val => val === '' || phoneRegex.test(val), { message: 'Enter a valid 10-digit phone number' })
    .optional()
    .or(z.literal('')),
  license_type_interest: z.string().optional(),
  message: z.string().trim().min(1, 'Message is required').max(2000),
});

type InquiryFormValues = z.infer<typeof inquirySchema>;

// All trainee-level license types
const ALL_LICENSE_TYPES = [
  'Certified General',
  'Certified Residential',
  'Licensed',
];

interface SupervisorInquiryFormProps {
  appraiserUserId: string;
  appraiserName: string;
  supervisorLicenseTypes?: string[] | null;
  isCertifiedGeneral?: boolean;
}

export function SupervisorInquiryForm({ appraiserUserId, appraiserName, supervisorLicenseTypes, isCertifiedGeneral }: SupervisorInquiryFormProps) {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<InquiryFormValues>({
    resolver: zodResolver(inquirySchema),
    defaultValues: { name: '', email: '', phone: '', license_type_interest: '', message: '' },
  });

  const licenseInterest = watch('license_type_interest');

  // Certified General appraisers can supervise all license types
  const availableLicenseTypes = isCertifiedGeneral
    ? ALL_LICENSE_TYPES
    : (supervisorLicenseTypes && supervisorLicenseTypes.length > 0 ? supervisorLicenseTypes : null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue('phone', formatted, { shouldValidate: false });
  };

  const onSubmit = async (data: InquiryFormValues) => {
    setSubmitting(true);
    try {
      const { error } = await supabase.from('supervisor_inquiries' as any).insert({
        appraiser_user_id: appraiserUserId,
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        license_type_interest: data.license_type_interest || null,
        message: data.message,
      } as any);

      if (error) throw error;

      // Send email + in-app notification (fire and forget)
      supabase.functions.invoke('supervisor-inquiry-notification', {
        body: {
          appraiserUserId,
          inquiry: {
            name: data.name,
            email: data.email,
            phone: data.phone || undefined,
            license_type_interest: data.license_type_interest || undefined,
            message: data.message,
          },
          turnstileToken,
        },
      }).catch(err => console.error('Notification error:', safeError(err)));

      setSubmitted(true);
    } catch (err: any) {
      toast({ title: 'Error sending inquiry', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-6 space-y-2">
        <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
        <p className="font-semibold text-foreground">Inquiry Sent!</p>
        <p className="text-sm text-muted-foreground">
          {appraiserName} will receive your message and can respond directly to your email.
        </p>
      </div>
    );
  }

  const phoneValue = watch('phone');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="inq-name">Your Name *</Label>
        <Input id="inq-name" {...register('name')} placeholder="Full name" />
        {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="inq-email">Email *</Label>
        <Input id="inq-email" type="email" {...register('email')} placeholder="your@email.com" />
        {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="inq-phone">Phone (optional)</Label>
        <Input
          id="inq-phone"
          type="tel"
          value={phoneValue || ''}
          onChange={handlePhoneChange}
          placeholder="(555) 123-4567"
        />
        {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
      </div>

      {availableLicenseTypes && availableLicenseTypes.length > 0 && (
        <div className="space-y-1.5">
          <Label>License Type Interest</Label>
          <Select value={licenseInterest} onValueChange={(v) => setValue('license_type_interest', v)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a license type" />
            </SelectTrigger>
            <SelectContent>
              {availableLicenseTypes.map(lt => (
                <SelectItem key={lt} value={lt}>{lt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="inq-message">Message *</Label>
        <Textarea
          id="inq-message"
          {...register('message')}
          placeholder={`Tell ${appraiserName} about your background and interest in becoming a trainee...`}
          rows={4}
        />
        {errors.message && <p className="text-xs text-destructive">{errors.message.message}</p>}
      </div>

      <TurnstileWidget onVerify={setTurnstileToken} className="flex justify-center" />

      <Button type="submit" className="w-full" disabled={submitting || !turnstileToken}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
        Send Inquiry
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        Your contact information will only be shared with {appraiserName}.
      </p>
    </form>
  );
}
